import React, { useState, useEffect } from "react";
import { Customer } from "../../types/customer";
import CustomerUpdateForm from "./CustomerUpdateForm";
import { fetchCustomerById } from "../../services/customerService";
import BrandManager from "../../features/brand/BrandManager";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useCustomerWorkspace } from "../../context/CustomerWorkspaceContext";
import { debug } from "../../utils/debugLogger";

const CustomerManager: React.FC = () => {
  const navigate = useNavigate();
  const { selectedCustomerId } = useCustomerWorkspace();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("profile");
  const { idToken } = useAuth();

  useEffect(() => {
    if (!selectedCustomerId || !idToken) {
      setSelectedCustomer(null);
      return;
    }
    setLoading(true);
    fetchCustomerById(selectedCustomerId, idToken)
      .then((response) => setSelectedCustomer(response))
      .catch((error) => {
        debug.error("Error fetching customer details:", error);
        setSelectedCustomer(null);
      })
      .finally(() => setLoading(false));
  }, [selectedCustomerId, idToken]);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-4 text-center">
        Business Profile Management
      </h1>

      <div className="flex justify-center mb-6">
        <button
          onClick={() => navigate("/onboarding")}
          className="px-4 py-2 rounded bg-brand-primary text-white hover:bg-brand-primary-hover transition-colors"
        >
          Create New Business Profile
        </button>
      </div>

      {loading ? (
        <p className="text-center">Loading business profile details...</p>
      ) : selectedCustomer ? (
        <>
          {/* Tab Navigation */}
          <div className="mt-4 border-b border-gray-200">
            <div className="flex space-x-4">
              <button
                onClick={() => setActiveTab("profile")}
                className={`py-2 px-4 ${
                  activeTab === "profile"
                    ? "border-b-2 border-brand-primary text-brand-primary font-medium"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Profile Details
              </button>
              <button
                onClick={() => setActiveTab("brand")}
                className={`py-2 px-4 ${
                  activeTab === "brand"
                    ? "border-b-2 border-brand-primary text-brand-primary font-medium"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Brand Management
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="mt-4">
            {activeTab === "profile" && (
              <div>
                <h2 className="text-lg font-semibold mb-2">
                  Business Profile Details
                </h2>
                <CustomerUpdateForm customer={selectedCustomer} />
              </div>
            )}

            {activeTab === "brand" && (
              <div>
                <h2 className="text-lg font-semibold mb-2 flex justify-between items-center">
                  Brand Information
                </h2>
                <BrandManager customerId={selectedCustomer.customer_id} />
              </div>
            )}
          </div>
        </>
      ) : (
        <p className="text-center text-gray-600">
          Please select a business profile from the sidebar to view or update
          details.
        </p>
      )}
    </div>
  );
};

export default CustomerManager;
